
import { Injectable, inject } from "@angular/core";
import _ from "lodash";
import { Cellstyle } from "~app/common/components/hot-doc/hotdoc/cell";
import { RuleErrorContext, RuleSeverityLevel, RuleValidationMessage, RuleVisibilityStrategy } from "~app/common/services/cqs/models/rule-validation-message";
import { VKDirektutbyte, VKDirektutbyteTradslagDTO } from "~app/features/virkeskop/models/tradslag/direktutbyte-models";
import { VKDirektutbyteTradslagHDCellStyles } from "../../models/tradslag/tradslag-models";
import { AtgardMediatorService } from "../atgard-mediator.service";
import { KontraktMediatorService } from "../kontrakt-mediator.service";
import { TradslagMediatorService } from "../tradslag-mediator.service";
import { VirkeskopValidationService } from "./virkeskop-validation.service";
@Injectable()
export class ForkalkylDirektutbyteClientValidationService {
    private readonly tradslagMediatorService = inject(TradslagMediatorService);
    private readonly validationService = inject(VirkeskopValidationService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly atgardMediatorService = inject(AtgardMediatorService);

    private direktutbyte: VKDirektutbyte;
    private importedDirektutbyteTradslag: VKDirektutbyteTradslagDTO[] = [];

    clearValidations(): void {
        this.validationService.updateClientSideValidation(
            false,
            this.createValidationMessage({
                messageId: "allTradslagColumnsMustHaveValues"
            })
        );
    }

    updateValidationTableFields(templateModel?: VKDirektutbyte): [Map<string, VKDirektutbyteTradslagHDCellStyles>, boolean] {
        this.direktutbyte = this.tradslagMediatorService.direktutbyte;
        this.importedDirektutbyteTradslag = this.tradslagMediatorService?.importedUtbyteTradslag;

        const resultingValidation = new Map<string, VKDirektutbyteTradslagHDCellStyles>([]);
        //Laddar in alla tradslag
        this.direktutbyte.direktutbyteTradslag.forEach((elem) => {
            resultingValidation.set(elem.tradslag.namn, {});
        });

        if(_.isNil(this.direktutbyte) || this.direktutbyte.direktutbyteTradslag.length === 0) {
            return [
                resultingValidation,
                false
            ];
        }

        let anyValidationErrors = false;

        this.direktutbyte.direktutbyteTradslag.forEach((elem) => {
            anyValidationErrors = this.validateValues(elem, resultingValidation);
        });

        this.validationService.updateClientSideValidation(
            anyValidationErrors,
            this.createValidationMessage({
                messageId: "allTradslagColumnsMustHaveValues",
                message: "För att ett direktutbyte ska kunna beräknas måste alla celler i trädslagstabellen ha ett värde.",
                severity: RuleSeverityLevel.warning
            })
        );

        this.applyTemplateColour(this.direktutbyte, templateModel, resultingValidation);

        if(this.importedDirektutbyteTradslag) {
            this.applyImportColor(this.direktutbyte, resultingValidation);
        }

        return [
            resultingValidation,
            anyValidationErrors
        ];
    }

    private validateValues(elem: VKDirektutbyteTradslagDTO, resultingValidation: Map<string, VKDirektutbyteTradslagHDCellStyles>): boolean {

        let anyValidationErrors = false;
        const diameterGrundytevagd = (elem.diameterGrundytevagd ?? 0);
        if(diameterGrundytevagd === 0) {
            resultingValidation.get(elem.tradslag.namn).diameterGrundytevagd = Cellstyle.Red;
            anyValidationErrors = true;
        }

        const grundyta = (elem.grundyta ?? 0);
        if(grundyta === 0) {
            resultingValidation.get(elem.tradslag.namn).grundyta = Cellstyle.Red;
            anyValidationErrors = true;
        }

        const hojdGrundyteVagd = (elem.hojdGrundyteVagd ?? 0);
        if(hojdGrundyteVagd === 0) {
            resultingValidation.get(elem.tradslag.namn).hojdGrundyteVagd = Cellstyle.Red;
            anyValidationErrors = true;
        }

        const m3skHa = (elem.m3skHa ?? 0);
        if(m3skHa === 0) {
            resultingValidation.get(elem.tradslag.namn).m3skHa = Cellstyle.Red;
            anyValidationErrors = true;
        }

        const medelstam = (elem.medelstam ?? 0);
        if(medelstam === 0) {
            resultingValidation.get(elem.tradslag.namn).medelstam = Cellstyle.Red;
            anyValidationErrors = true;
        }

        const stammarperHa = (elem.stammarperHa ?? 0);
        if(stammarperHa === 0) {
            resultingValidation.get(elem.tradslag.namn).stammarperHa = Cellstyle.Red;
            anyValidationErrors = true;
        }

        return anyValidationErrors;
    }

    private applyTemplateColour(direktutbyte: VKDirektutbyte, templateModel: VKDirektutbyte, resultingValidation: Map<string, VKDirektutbyteTradslagHDCellStyles>) {
        if(_.isNil(templateModel) || templateModel.direktutbyteTradslag.length === 0) {
            return;
        }

        direktutbyte.direktutbyteTradslag.forEach((elem) => {
            const direktutbyteTradslag = templateModel.direktutbyteTradslag.find(x => x.tradslag.id === elem.tradslag.id);
            if (_.isNil(direktutbyteTradslag)) {
                return;
            }

            if (elem.tradslag.id === direktutbyteTradslag.tradslag.id) {
                // eslint-disable-next-line no-bitwise
                resultingValidation.get(elem.tradslag.namn).tradslag |= Cellstyle.Blue;
            }

        });
    }

    private applyImportColor(direktutbyte: VKDirektutbyte,
        resultingValidation: Map<string, VKDirektutbyteTradslagHDCellStyles>) {

        direktutbyte.direktutbyteTradslag.forEach((currentModel) => {
            const importedTradslag = this.importedDirektutbyteTradslag?.find(t => t.tradslag.id === currentModel.tradslag.id);
            this.applyImportCellColor(importedTradslag, currentModel, resultingValidation);
        });
    }

    private applyImportCellColor(
        importedTradslag: VKDirektutbyteTradslagDTO,
        current: VKDirektutbyteTradslagDTO,
        resultingValidation: Map<string, VKDirektutbyteTradslagHDCellStyles>) {

        if(importedTradslag) {
            const propertiesToCheck: (keyof VKDirektutbyteTradslagDTO)[] = [
                "diameterGrundytevagd",
                "grundyta",
                "hojdGrundyteVagd",
                "m3skHa",
                "medelstam",
                "stammarperHa"
            ];

            let anySameAsImport = false;

            propertiesToCheck.forEach(property => {
                if (importedTradslag[property] === current[property] && !_.isNil(importedTradslag[property])) {
                    resultingValidation.get(current.tradslag.namn)[property] = Cellstyle.Green;
                    anySameAsImport = true;
                }
            });

            if (anySameAsImport) {
                resultingValidation.get(current.tradslag.namn).tradslag = Cellstyle.Green;
            }
        }
    }

    private get kontrakt() {
        return this.kontraktMediatorService.kontrakt;
    }

    private get atgardGuid() {
        return this.atgardMediatorService.atgard.guid;
    }

    private createValidationMessage(message: Partial<RuleValidationMessage>): RuleValidationMessage {
        return {
            severity: RuleSeverityLevel.info,
            strategy: RuleVisibilityStrategy.alwaysShow,
            context: RuleErrorContext.save,
            entityType: "tradslag",
            entityGuids: [
                this.kontrakt.guid,
                this.atgardGuid
            ],
            message: "",
            messageId: "",
            ...message
        };
    }

}
